/* « Ajouter un dossier » / « Ajouter un membre » — investigator dossier management
 * dialogs (Suivi tab). The médecin is authoritative: every SUBJECT role of the study
 * is addable — data-only roles (Enfant) become plain data rows, authenticative roles
 * (Mère/Père) become credential-less placeholders the parent will CLAIM at signup.
 *
 * Roles come from the cached welcome payload (the app's only role catalog); creation
 * happens server-side (allowlist-validated numéro; 409 when the dossier already has
 * members anywhere — a typo must not silently extend another site's family).
 */
import { ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { IonModal } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import {
  ModalCard as KitModalCard,
  ModalTitle,
  ModalDescription,
  ModalActions,
  ModalPrimaryButton,
  ModalQuietButton,
  ModalError,
  ModalFields,
} from "../../components/atoms/ModalKit";
import Icon from "../../components/atoms/Icon";
import { TextField } from "../../components/atoms/LoginTextField";
import { useConfig } from "../../contexts/ConfigContext";
import { useStudySlug } from "../../hooks/useStudySlug";
import { getWelcomeInfo } from "../../services/weclome.service";
import { postAddSuiviGroupMember, postCreateSuiviGroup } from "../../services/suivi.service";
import { getManageableSubjectRoles } from "../../helper/familyComposition";
import { AttributeEnum, ProfileType } from "../../types/profile";

// Shared: the study's subject-role catalog, from the (cached, deduped) welcome payload.
const useSubjectRoles = (): ProfileType[] => {
  const study_slug = useStudySlug();
  const {
    i18n: { language },
  } = useTranslation();
  const [roles, setRoles] = useState<ProfileType[]>([]);

  useEffect(() => {
    let cancelled = false;
    getWelcomeInfo({ slug: study_slug, lang: language })
      .then(({ data }) => {
        if (!cancelled) setRoles(getManageableSubjectRoles(data.roles));
      })
      .catch(() => {
        /* fail-soft: the modal simply shows no role rows; the server validates anyway */
      });
    return () => {
      cancelled = true;
    };
  }, [study_slug, language]);

  return roles;
};

const roleMaxCount = (role: ProfileType): number =>
  role.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] === false
    ? role.attributes?.auto_create_max ?? 9
    : 9;

const mapCreateError = (
  error: any,
  t: (key: string) => string
): string => {
  const message = error?.response?.data?.message ?? "";
  if (message === "External id not recognized") {
    return t("home_inscription_error_EXTERNAL_ID_UNKNOWN");
  }
  if (error?.response?.status === 409) return t("suivi_dossier_exists");
  return t("common_server_error_desc");
};

interface CreateDossierModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  // Called with the created group's key — the caller refetches and navigates.
  onCreated: (groupKey: string) => void;
}

export const CreateDossierModal = ({
  isOpen,
  onDismiss,
  onCreated,
}: CreateDossierModalProps): ReactElement => {
  const { t } = useTranslation();
  const { study } = useConfig();
  const roles = useSubjectRoles();

  const [numero, setNumero] = useState("");
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed each data-only role at 1 (the common case: one enfant) once roles arrive.
  useEffect(() => {
    setCounts((current) => {
      const seeded = { ...current };
      roles.forEach((role) => {
        if (seeded[role.id ?? -1] === undefined) {
          seeded[role.id ?? -1] =
            role.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] === false ? 1 : 0;
        }
      });
      return seeded;
    });
  }, [roles]);

  const total = useMemo(
    () => Object.values(counts).reduce((sum, count) => sum + (count || 0), 0),
    [counts]
  );

  const bump = (role: ProfileType, delta: number): void => {
    const roleId = role.id ?? -1;
    setCounts((current) => {
      const next = Math.min(
        roleMaxCount(role),
        Math.max(0, (current[roleId] ?? 0) + delta)
      );
      return { ...current, [roleId]: next };
    });
  };

  const handleCreate = async (): Promise<void> => {
    const trimmed = numero.trim();
    if (!trimmed) {
      setError(t("suivi_dossier_numero_required"));
      return;
    }
    if (total < 1) {
      setError(t("suivi_dossier_members_required"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data } = await postCreateSuiviGroup(
        { studyId: study.id },
        {
          external_id: trimmed,
          members: roles
            .filter((role) => (counts[role.id ?? -1] ?? 0) > 0)
            .map((role) => ({
              study_role_id: role.id as number,
              count: counts[role.id ?? -1],
            })),
        }
      );
      onCreated(data.group_key);
    } catch (e) {
      setError(mapCreateError(e, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      className="resonance-modal"
      backdropDismiss
      onWillDismiss={onDismiss}
    >
      <WideModalCard>
        <ModalTitle>{t("suivi_new_dossier_title")}</ModalTitle>
        <CancelButton icon={closeOutline} onClick={onDismiss} />
        <ModalDescription>{t("suivi_new_dossier_description")}</ModalDescription>

        <ModalFields>
          <TextField
            type="text"
            onChange={(text) => setNumero(text)}
            value={numero}
            placeholder={t("suivi_dossier_numero_label")}
            label={t("suivi_dossier_numero_label")}
          />
        </ModalFields>

        {roles.map((role) => (
          <StepperRow key={role.id}>
            <StepperLabel>{role.profile}</StepperLabel>
            <Stepper>
              <StepperButton
                type="button"
                disabled={(counts[role.id ?? -1] ?? 0) <= 0}
                onClick={() => bump(role, -1)}
              >
                −
              </StepperButton>
              <StepperValue>{counts[role.id ?? -1] ?? 0}</StepperValue>
              <StepperButton
                type="button"
                disabled={(counts[role.id ?? -1] ?? 0) >= roleMaxCount(role)}
                onClick={() => bump(role, +1)}
              >
                +
              </StepperButton>
            </Stepper>
          </StepperRow>
        ))}

        {error && <ModalError>{error}</ModalError>}

        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
          <ModalPrimaryButton onClick={handleCreate} disabled={saving}>
            {t("suivi_new_dossier_create")}
          </ModalPrimaryButton>
        </ModalActions>
      </WideModalCard>
    </IonModal>
  );
};

interface AddMemberModalProps {
  isOpen: boolean;
  groupKey: string;
  onDismiss: () => void;
  onAdded: () => void;
}

export const AddMemberModal = ({
  isOpen,
  groupKey,
  onDismiss,
  onAdded,
}: AddMemberModalProps): ReactElement => {
  const { t } = useTranslation();
  const { study } = useConfig();
  const roles = useSubjectRoles();

  const [roleId, setRoleId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A single manageable role needs no choosing — preselect it.
  useEffect(() => {
    if (roles.length === 1 && roles[0].id != null) setRoleId(String(roles[0].id));
  }, [roles]);

  const handleAdd = async (): Promise<void> => {
    if (!roleId) {
      setError(t("suivi_add_member_role_required"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await postAddSuiviGroupMember({
        studyId: study.id,
        groupKey,
        studyRoleId: Number(roleId),
      });
      onAdded();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? t("common_server_error_desc"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      className="resonance-modal"
      backdropDismiss
      onWillDismiss={onDismiss}
    >
      <KitModalCard>
        <ModalTitle>{t("suivi_add_member_title")}</ModalTitle>
        {/* Roles laid out flat (no dropdown): one tap selects, « Ajouter » confirms. */}
        <RoleChoices>
          {roles.map((role) => (
            <RoleChoice
              key={role.id}
              type="button"
              $selected={roleId === String(role.id)}
              aria-pressed={roleId === String(role.id)}
              onClick={() => {
                setRoleId(String(role.id));
                setError(null);
              }}
            >
              {role.profile}
            </RoleChoice>
          ))}
        </RoleChoices>
        {error && <ModalError>{error}</ModalError>}
        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
          <ModalPrimaryButton onClick={handleAdd} disabled={saving}>
            {t("suivi_add_member_confirm")}
          </ModalPrimaryButton>
        </ModalActions>
      </KitModalCard>
    </IonModal>
  );
};

const WideModalCard = styled(KitModalCard)`
  margin: auto 0;
  position: relative;
  max-width: 600px;
`;

const CancelButton = styled(Icon)`
  position: absolute;
  top: 10px;
  right: 10px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.45);
  font-size: 20px;
  &:active {
    opacity: 0.5;
  }
`;

const StepperRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.08);
`;

const StepperLabel = styled.span`
  font-size: 15px;
  color: var(--app-color-text, #0f172a);
`;

const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const StepperButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.2);
  background: transparent;
  font-size: 18px;
  line-height: 1;
  color: var(--app-color-text, #0f172a);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const StepperValue = styled.span`
  min-width: 20px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--app-color-text, #0f172a);
`;

/* Flat, directly-tappable role rows (ModalFields' field language: text-alpha
   hairline at rest, accent frame + soft tint when selected). */
const RoleChoices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const RoleChoice = styled.button<{ $selected: boolean }>`
  width: 100%;
  min-height: 52px;
  padding: 8px 16px;
  text-align: left;
  font-size: 16px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  color: var(--app-color-text, #041723);
  background-color: ${({ $selected }) =>
    $selected
      ? "rgba(var(--app-color-accent-rgb, 66, 133, 244), 0.08)"
      : "rgba(var(--app-color-text-rgb, 4, 23, 35), 0.04)"};
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "var(--app-color-accent, #4285f4)"
        : "rgba(var(--app-color-text-rgb, 4, 23, 35), 0.12)"};
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 120ms ease-out, background-color 120ms ease-out;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.7;
  }
`;

export default CreateDossierModal;
